<script>
    import httpService from '@/services/core/http.service';
    import store from '@/store/global.store';
    import AdminGrid from '@/components/shared/AdminGrid';
    import { DxColumn, DxItem } from 'devextreme-vue/data-grid';
    import modalService from '@/services/core/modal.service';
    import CompanyModal from '@/components/modals/company/CompanyModal';
    import ConfirmModal from '@/components/modals/ConfirmModal';
    import utilsService from '@/services/core/utils.service';
    import UndeleteBatteriesModal from '@/components/modals/company/UndeleteBatteriesModal';
    import CopyBatteriesModal from '@/components/modals/company/CopyBatteriesModal';
    import CopyCompanySettingsModal from '@/components/modals/company/CopyCompanySettingsModal';
    import CloneFacilityModal from '@/components/modals/company/CloneFacilityModal';
    import CloneFacilityHistoryModal from '@/components/modals/company/CloneFacilityHistoryModal';
    import OpsChangeInboxEmailsModal from '@/components/modals/company/OpsChangeInboxEmailsModal';

    export default {
        components: {
            AdminGrid,
            DxColumn,
            DxItem
        },
        data() {
            return {
                dataSource: httpService.getDxDataSource('companies/grid')
            };
        },
        computed: {
            isReadOnly() {
                return store.getters.currentUser()?.isReadOnly === true;
            },
            hasBillingPermission() {
                const { permissions = {} } = store.getters.currentUser() || {};
                return permissions?.hasBillingAccess === true;
            }
        },
        methods: {
            onCellPrepared(e) {
                if (e.rowType === 'data' && !e.data.status) {
                    e.cellElement.style.color = '#ccc';
                }
            },
            add() {
                modalService.show(CompanyModal, {
                    item: { id: 0, country: 'US', maxBatteriesPerUserAllowed: 100, subscriptionType: 1, user: { email: 'noemail@.com' } },
                    onSuccess: () => {
                        this.$refs['companyGrid'].refresh();
                    }
                });
            },
            onEdit(item) {
                const url = `companies/${item.id}`;
                httpService.get(url).then(company => {
                    modalService.show(CompanyModal, {
                        item: company,
                        onSuccess: () => {
                            this.$refs['companyGrid'].refresh();
                        }
                    });
                });
            },
            changeStatus(item) {
                if (this.isReadOnly) return;

                modalService.show(ConfirmModal, {
                    title: item.status ? 'Disable Company' : 'Enable Company',
                    msg: `Are you sure you want to ${item.status ? 'DISABLE' : 'ENABLE'} company ${item.name}?`,
                    btnTextOk: item.status ? 'Disable' : 'Enable',
                    onSuccess: () => {
                        const url = `companies/${item.id}/status`;
                        httpService.put(url).then(() => {
                            utilsService.showToast('Company was saved', 'success');
                            this.$refs['companyGrid'].refresh();
                        });
                    }
                });
            },
            showBilling(item) {
                window.open(`https://billing.greasebook.com/companies/${item.id}`, '_blank', 'noopener');
            },
            downloadWellUsageReport() {
                httpService.downloadReport('companies/well-usage/download').then();
            },
            downloadFreshbookCompanies() {
                httpService.downloadReport('companies/freshbooks-companies/download').then();
            },
            downloadFreshbookItems() {
                httpService.downloadReport('companies/freshbooks-items/download').then();
            },
            downloadBillingSettings() {
                httpService.downloadReport('companies/download-billing-settings').then();
            },
            undeleteBatteries(item) {
                modalService.show(UndeleteBatteriesModal, {
                    item: item.id
                });
            },
            copyBatteries(item) {
                modalService.show(CopyBatteriesModal, {
                    item: item.id
                });
            },
            copySettings(item) {
                modalService.show(CopyCompanySettingsModal, {
                    item: { id: item.id, name: item.name }
                });
            },
            cloneFacility(item) {
                modalService.show(CloneFacilityModal, {
                    item: item.id
                });
            },
            cloneHistory() {
                modalService.show(CloneFacilityHistoryModal, {});
            },
            opsChangeEmails(item) {
                modalService.show(OpsChangeInboxEmailsModal, {
                    companyId: item.id,
                    companyName: item.name
                });
            },
            onFileChange(e) {
                const file = e.target.files[0];

                httpService.postFile('companies/freshbooks-ids', file).finally(() => (this.$refs.fileInput.value = ''));
            }
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi bi-building"></i>
            Companies
        </h3>
    </div>
    <div class="main-content-body">
        <!-- <label for="updateFreshbooksIds" class="cursor-pointer mb-0">
            <i class="fas fa-upload"></i>
            Update Freshbooks Ids
        </label>
        <input
            ref="fileInput"
            id="updateFreshbooksIds"
            class="d-none"
            type="file"
            :accept="`.xlsx`"
            @change="onFileChange"
        />-->
        <AdminGrid :data-source="dataSource" storage-key="companies" ref="companyGrid" @cell-prepared="onCellPrepared">
            <template #toolbar>
                <DxItem v-if="!isReadOnly" location="after" widget="dxButton" :options="{ text: 'Add', onClick: add }" />
                <DxItem
                    v-if="!isReadOnly"
                    location="after"
                    widget="dxButton"
                    :options="{ text: 'Well Usage Report', onClick: downloadWellUsageReport }"
                />
                <DxItem
                    v-if="!isReadOnly"
                    location="after"
                    widget="dxButton"
                    :options="{ text: 'Freshbooks Companies', onClick: downloadFreshbookCompanies }"
                />
                <!-- <DxItem
                    location="after"
                    widget="dxButton"
                    :options="{ text: 'Freshbooks Items', onClick: downloadFreshbookItems }"
                />-->

                <DxItem v-if="!isReadOnly" location="after" widget="dxButton" :options="{ text: 'Clone History', onClick: cloneHistory }" />
            </template>
            <DxColumn data-field="id" sort-order="desc" />
            <DxColumn data-field="name" />
            <DxColumn data-field="state" />
            <DxColumn data-field="publicApiWrite" :width="150" caption="API Write" />
            <DxColumn data-field="lastPublicApiCallUtc" caption="API Last Seen" data-type="date" format="MM/dd/yyyy hh:mm a" :width="170" />
            <DxColumn data-field="daysFromLastApiCall" caption="API Days Ago" data-type="number" :width="120" />
            <DxColumn data-field="hasTinyPumper" :width="150" caption="Tiny Pumper" />

            <template #actions="{ item }">
                <button
                    v-if="item.data.status && !isReadOnly"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    style="font-size: 1.2rem"
                    title="Copy Batteries"
                    @click="copyBatteries(item.data)"
                >
                    <i class="bi bi-clipboard"></i>
                </button>

                <button
                    v-if="item.data.status && !isReadOnly"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    style="font-size: 1.2rem"
                    title="Copy Settings to another company"
                    @click="copySettings(item.data)"
                >
                    <i class="bi bi-sliders"></i>
                </button>

                <button
                    v-if="item.data.status && !isReadOnly"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    style="font-size: 1.2rem"
                    title="Clone Facility"
                    @click="cloneFacility(item.data)"
                >
                    <i class="bi bi-files"></i>
                </button>

                <button
                    v-if="item.data.status && !isReadOnly"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    style="font-size: 1.2rem"
                    title="Undelete Batteries"
                    @click="undeleteBatteries(item.data)"
                >
                    <i class="bi bi-arrow-counterclockwise"></i>
                </button>

                <button v-if="item.data.status" type="button" class="btn btn-link py-0 lh-1" title="Edit" @click="onEdit(item.data)">
                    <i class="bi bi-pencil"></i>
                </button>

                <button
                    v-if="item.data.status"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    title="Ops change inbox emails"
                    @click="opsChangeEmails(item.data)"
                >
                    <i class="bi bi-envelope-at"></i>
                </button>

                <button
                    v-if="item.data.status && !isReadOnly && hasBillingPermission"
                    type="button"
                    class="btn btn-link py-0 lh-1 text-success"
                    title="Billing"
                    @click="showBilling(item.data)"
                >
                    <i class="bi bi-receipt"></i>
                </button>

                <button
                    v-if="item.data.status"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    style="font-size: 1.2rem"
                    title="Disable"
                    @click="changeStatus(item.data)"
                    :readonly="isReadOnly"
                >
                    <i class="bi bi-toggle-on"></i>
                </button>

                <button
                    v-if="!item.data.status"
                    type="button"
                    class="btn btn-link py-0 lh-1"
                    style="font-size: 1.2rem"
                    title="Enable"
                    @click="changeStatus(item.data)"
                    :readonly="isReadOnly"
                >
                    <i class="bi bi-toggle-off"></i>
                </button>
            </template>
        </AdminGrid>
    </div>
</template>
